import React, { useState, useMemo } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Warehouse, ArrowDownToLine, ArrowUpFromLine, AlertTriangle, Package } from "lucide-react";

const fetcher = (u) => api.get(u).then(r=>r.data);
const num = (v, d=2) => (Math.round((v||0) * Math.pow(10,d)) / Math.pow(10,d)).toLocaleString();

// ---------- Stock Page ----------
export const RMStockPage = () => {
  const { data: stock, isLoading } = useSWR("/rm-stock", fetcher, { refreshInterval: 15000 });
  const [q, setQ] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const rows = (stock||[]).filter(r => {
    if (unitFilter && r.unit_id !== unitFilter) return false;
    if (!q) return true;
    const s = `${r.name} ${r.code} ${r.grade} ${r.material_type} ${r.warehouse_name}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });
  const totalValue = rows.reduce((a,r)=>a + (r.value||0), 0);
  const belowReorder = rows.filter(r => r.below_reorder).length;
  const units = useMemo(() => {
    const map = {};
    (stock||[]).forEach(r => { if (r.unit_id) map[r.unit_id] = r.unit_name || r.unit_id; });
    return Object.entries(map);
  }, [stock]);

  return (
    <div className="space-y-4" data-testid="rm-stock-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Warehouse size={22} className="text-sky-600"/> RM Stock</h1>
          <p className="text-sm text-slate-500">Live raw material stock per warehouse. Valued at last purchase rate.</p>
        </div>
        <div className="flex gap-2">
          <select data-testid="rm-stock-unit-filter" value={unitFilter} onChange={e=>setUnitFilter(e.target.value)} className="text-sm px-3 py-1.5 rounded-md border border-slate-200 bg-white">
            <option value="">All units</option>
            {units.map(([id,name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <input data-testid="rm-stock-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search material / warehouse..." className="text-sm px-3 py-1.5 rounded-md border border-slate-200 bg-white"/>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="kpi-card"><div className="kpi-label">Lines</div><div className="kpi-value" data-testid="kpi-rm-lines">{rows.length}</div></div>
        <div className="kpi-card status-good"><div className="kpi-label">Total Value</div><div className="kpi-value" data-testid="kpi-rm-value">₹{num(totalValue, 0)}</div></div>
        <div className={`kpi-card ${belowReorder>0?"status-warn":""}`}><div className="kpi-label">Below Reorder</div><div className="kpi-value" data-testid="kpi-rm-below-reorder">{belowReorder}</div></div>
        <div className="kpi-card"><div className="kpi-label">Warehouses</div><div className="kpi-value">{new Set(rows.map(r=>r.warehouse_id)).size}</div></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-auto max-h-[65vh]">
          <table className="data-table" data-testid="rm-stock-table">
            <thead><tr>
              <th>Material</th><th>Type</th><th>Grade</th><th>Warehouse</th><th>Unit</th>
              <th className="text-right">Stock (kg)</th><th className="text-right">Last Rate</th><th className="text-right">Value</th><th className="text-right">Reorder Lvl</th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={9} className="text-center text-slate-400 py-6">Loading...</td></tr>}
              {!isLoading && rows.length===0 && <tr><td colSpan={9} className="text-center text-slate-400 py-6">No stock yet. Receive via GRN or create opening stock via Data Import.</td></tr>}
              {rows.map(r => (
                <tr key={`${r.material_id}-${r.warehouse_id}`} data-testid={`rm-stock-row-${r.material_id}-${r.warehouse_id}`} className={r.below_reorder ? "bg-amber-50/60" : ""}>
                  <td>
                    <div className="font-medium text-slate-900">{r.name}</div>
                    <div className="text-[10px] font-mono text-slate-500">{r.code}</div>
                  </td>
                  <td><span className="pill pill-idle">{r.material_type || "—"}</span></td>
                  <td className="text-xs">{r.grade || "—"}</td>
                  <td className="text-sm">{r.warehouse_name}</td>
                  <td className="text-xs text-slate-500">{r.unit_name || "—"}</td>
                  <td className={`text-right font-mono font-semibold ${r.below_reorder?"text-amber-700":""}`}>{num(r.qty_kg, 2)}</td>
                  <td className="text-right font-mono text-xs">₹{num(r.last_rate)}</td>
                  <td className="text-right font-mono text-xs">₹{num(r.value, 0)}</td>
                  <td className="text-right text-xs text-slate-500">
                    {r.reorder_level ? num(r.reorder_level, 0) : "—"}
                    {r.below_reorder && <AlertTriangle size={12} className="inline ml-1 text-amber-700"/>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------- Shared material + lot picker ----------
const useRMData = () => {
  const { data: materials } = useSWR("/raw_materials", fetcher);
  const { data: warehouses } = useSWR("/warehouses", fetcher);
  const { data: machines } = useSWR("/machines", fetcher);
  const { data: pos } = useSWR("/production-orders", fetcher);
  return { materials: materials||[], warehouses: warehouses||[], machines: machines||[], pos: pos||[] };
};

// ---------- Issue Page ----------
export const RMIssuePage = () => {
  const { materials, warehouses, machines, pos } = useRMData();
  const { data: issues } = useSWR("/rm-issues", fetcher, { refreshInterval: 10000 });
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ shift: "A" });
  const [busy, setBusy] = useState(false);
  const lotsKey = f.material_id ? `/rm-lots?material_id=${f.material_id}${f.warehouse_id ? "&warehouse_id="+f.warehouse_id : ""}` : null;
  const { data: lots } = useSWR(lotsKey, fetcher);
  const mMap = Object.fromEntries(materials.map(m=>[m.material_id, m]));
  const wMap = Object.fromEntries(warehouses.map(w=>[w.warehouse_id, w]));
  const mcMap = Object.fromEntries(machines.map(m=>[m.machine_id, m]));
  const poMap = Object.fromEntries(pos.map(p=>[p.po_id, p]));

  const openPOs = pos.filter(p => ["PLANNED","RELEASED","RUNNING","PAUSED"].includes(p.status));

  const submit = async () => {
    if (!f.material_id || !f.warehouse_id || !f.batch || !f.qty_kg) { toast.error("Material, warehouse, lot and qty are required"); return; }
    setBusy(true);
    try {
      await api.post("/rm-issues", { ...f, qty_kg: Number(f.qty_kg) });
      toast.success("RM issued");
      setShow(false); setF({ shift: "A" });
      mutate("/rm-issues"); mutate("/rm-stock"); mutate(lotsKey);
    } catch (e) { toast.error(e.response?.data?.detail || "Issue failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="rm-issue-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ArrowUpFromLine size={22} className="text-sky-600"/> RM Issues</h1>
          <p className="text-sm text-slate-500">{(issues||[]).length} issues · issue raw material to a machine / production order / shift</p>
        </div>
        <button data-testid="btn-create-rm-issue" onClick={()=>setShow(true)} className="bg-sky-600 hover:bg-sky-700 text-white text-sm px-3 py-1.5 rounded-md">+ Issue RM</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-auto max-h-[65vh]">
          <table className="data-table" data-testid="rm-issues-table">
            <thead><tr><th>Issue No</th><th>Date</th><th>Material</th><th>Lot</th><th>Warehouse</th><th className="text-right">Qty (kg)</th><th>Machine</th><th>PO</th><th>Shift</th><th>By</th></tr></thead>
            <tbody>
              {(issues||[]).length===0 && <tr><td colSpan={10} className="text-center text-slate-400 py-6">No issues yet.</td></tr>}
              {(issues||[]).map(i => (
                <tr key={i.issue_id} data-testid={`rm-issue-row-${i.issue_id}`}>
                  <td className="font-mono text-xs">{i.issue_no}</td>
                  <td className="text-xs">{(i.date||"").slice(0,16).replace("T"," ")}</td>
                  <td>{mMap[i.material_id]?.name || i.material_id}</td>
                  <td className="font-mono text-xs">{i.batch}</td>
                  <td className="text-xs">{wMap[i.warehouse_id]?.name || i.warehouse_id}</td>
                  <td className="text-right font-mono">{num(i.qty_kg, 2)}</td>
                  <td className="text-xs">{mcMap[i.machine_id]?.name || i.machine_id || "—"}</td>
                  <td className="text-xs font-mono">{poMap[i.po_id]?.po_no || i.po_id || "—"}</td>
                  <td><span className="pill pill-idle">{i.shift}</span></td>
                  <td className="text-xs text-slate-500">{i.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto" data-testid="rm-issue-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Issue Raw Material</h3>
              <button onClick={()=>setShow(false)} className="text-slate-400 hover:text-slate-900">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Material *</label>
                <select data-testid="input-rm-issue-material" value={f.material_id||""} onChange={e=>setF({...f, material_id: e.target.value, batch: "", warehouse_id: ""})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5">
                  <option value="">Select...</option>
                  {materials.map(m => <option key={m.material_id} value={m.material_id}>{m.code ? m.code+" · " : ""}{m.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Lot / Batch *</label>
                <select data-testid="input-rm-issue-batch" value={f.batch||""} onChange={e=>{
                  const lot = (lots||[]).find(l=>l.batch===e.target.value);
                  setF({...f, batch: e.target.value, warehouse_id: lot?.warehouse_id || f.warehouse_id});
                }} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5" disabled={!f.material_id}>
                  <option value="">Select lot...</option>
                  {(lots||[]).map(l => <option key={`${l.batch}-${l.warehouse_id}`} value={l.batch}>{l.batch} · {num(l.available_kg,2)} kg · {wMap[l.warehouse_id]?.name || l.warehouse_id}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Qty (kg) *</label>
                <input data-testid="input-rm-issue-qty" type="number" step="0.01" value={f.qty_kg||""} onChange={e=>setF({...f, qty_kg: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"/>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Machine</label>
                <select data-testid="input-rm-issue-machine" value={f.machine_id||""} onChange={e=>setF({...f, machine_id: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5">
                  <option value="">—</option>
                  {machines.map(m => <option key={m.machine_id} value={m.machine_id}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Production Order</label>
                <select data-testid="input-rm-issue-po" value={f.po_id||""} onChange={e=>setF({...f, po_id: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5">
                  <option value="">—</option>
                  {openPOs.map(p => <option key={p.po_id} value={p.po_id}>{p.po_no}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Shift *</label>
                <select data-testid="input-rm-issue-shift" value={f.shift||"A"} onChange={e=>setF({...f, shift: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5">
                  <option>A</option><option>B</option><option>C</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Operator</label>
                <input data-testid="input-rm-issue-operator" value={f.operator||""} onChange={e=>setF({...f, operator: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"/>
              </div>
              <div className="col-span-2">
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Remarks</label>
                <textarea data-testid="input-rm-issue-remarks" value={f.remarks||""} onChange={e=>setF({...f, remarks: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5" rows={2}/>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={()=>setShow(false)} className="text-sm px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
              <button data-testid="btn-submit-rm-issue" onClick={submit} disabled={busy} className="text-sm px-3 py-1.5 rounded-md bg-sky-600 text-white disabled:opacity-50">{busy?"Issuing...":"Issue"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Return Page ----------
export const RMReturnPage = () => {
  const { materials, warehouses, machines, pos } = useRMData();
  const { data: returns } = useSWR("/rm-returns", fetcher, { refreshInterval: 10000 });
  const { data: issues } = useSWR("/rm-issues", fetcher);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const mMap = Object.fromEntries(materials.map(m=>[m.material_id, m]));
  const wMap = Object.fromEntries(warehouses.map(w=>[w.warehouse_id, w]));
  const mcMap = Object.fromEntries(machines.map(m=>[m.machine_id, m]));
  const poMap = Object.fromEntries(pos.map(p=>[p.po_id, p]));

  const pickIssue = (issue_id) => {
    const iss = (issues||[]).find(i=>i.issue_id === issue_id);
    if (!iss) { setF({ issue_id }); return; }
    setF({
      issue_id, material_id: iss.material_id, warehouse_id: iss.warehouse_id,
      batch: iss.batch, machine_id: iss.machine_id, po_id: iss.po_id, uom: iss.uom || "kg",
    });
  };

  const submit = async () => {
    if (!f.material_id || !f.warehouse_id || !f.batch || !f.qty_kg) { toast.error("Material, warehouse, lot and qty are required"); return; }
    setBusy(true);
    try {
      await api.post("/rm-returns", { ...f, qty_kg: Number(f.qty_kg) });
      toast.success("RM returned to store");
      setShow(false); setF({});
      mutate("/rm-returns"); mutate("/rm-stock");
    } catch (e) { toast.error(e.response?.data?.detail || "Return failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="rm-return-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ArrowDownToLine size={22} className="text-sky-600"/> RM Returns</h1>
          <p className="text-sm text-slate-500">{(returns||[]).length} returns · return unused material back to store</p>
        </div>
        <button data-testid="btn-create-rm-return" onClick={()=>setShow(true)} className="bg-sky-600 hover:bg-sky-700 text-white text-sm px-3 py-1.5 rounded-md">+ Return RM</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-auto max-h-[65vh]">
          <table className="data-table" data-testid="rm-returns-table">
            <thead><tr><th>Return No</th><th>Date</th><th>Material</th><th>Lot</th><th>Warehouse</th><th className="text-right">Qty (kg)</th><th>Machine</th><th>Issue Ref</th><th>Reason</th></tr></thead>
            <tbody>
              {(returns||[]).length===0 && <tr><td colSpan={9} className="text-center text-slate-400 py-6">No returns yet.</td></tr>}
              {(returns||[]).map(r => (
                <tr key={r.return_id} data-testid={`rm-return-row-${r.return_id}`}>
                  <td className="font-mono text-xs">{r.return_no}</td>
                  <td className="text-xs">{(r.date||"").slice(0,16).replace("T"," ")}</td>
                  <td>{mMap[r.material_id]?.name || r.material_id}</td>
                  <td className="font-mono text-xs">{r.batch}</td>
                  <td className="text-xs">{wMap[r.warehouse_id]?.name || r.warehouse_id}</td>
                  <td className="text-right font-mono">{num(r.qty_kg, 2)}</td>
                  <td className="text-xs">{mcMap[r.machine_id]?.name || "—"}</td>
                  <td className="font-mono text-[10px] text-slate-500">{r.issue_id || "—"}</td>
                  <td className="text-xs">{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto" data-testid="rm-return-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Return Raw Material</h3>
              <button onClick={()=>setShow(false)} className="text-slate-400 hover:text-slate-900">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Reference Issue (optional)</label>
                <select data-testid="input-rm-return-issue" value={f.issue_id||""} onChange={e=>pickIssue(e.target.value)} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5">
                  <option value="">Manual return (no issue ref)</option>
                  {(issues||[]).slice(0,100).map(i => <option key={i.issue_id} value={i.issue_id}>{i.issue_no} · {mMap[i.material_id]?.name} · {i.batch} · {num(i.qty_kg,2)}kg</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Material *</label>
                <select data-testid="input-rm-return-material" value={f.material_id||""} onChange={e=>setF({...f, material_id: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5">
                  <option value="">Select...</option>
                  {materials.map(m => <option key={m.material_id} value={m.material_id}>{m.code ? m.code+" · " : ""}{m.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Warehouse *</label>
                <select data-testid="input-rm-return-warehouse" value={f.warehouse_id||""} onChange={e=>setF({...f, warehouse_id: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5">
                  <option value="">Select...</option>
                  {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Lot / Batch *</label>
                <input data-testid="input-rm-return-batch" value={f.batch||""} onChange={e=>setF({...f, batch: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5" placeholder="e.g. B-abc123"/>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Qty (kg) *</label>
                <input data-testid="input-rm-return-qty" type="number" step="0.01" value={f.qty_kg||""} onChange={e=>setF({...f, qty_kg: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"/>
              </div>
              <div className="col-span-2">
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Reason</label>
                <input data-testid="input-rm-return-reason" value={f.reason||""} onChange={e=>setF({...f, reason: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5" placeholder="Unused material / wrong material / etc."/>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={()=>setShow(false)} className="text-sm px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
              <button data-testid="btn-submit-rm-return" onClick={submit} disabled={busy} className="text-sm px-3 py-1.5 rounded-md bg-sky-600 text-white disabled:opacity-50">{busy?"Returning...":"Return to Store"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
